import { getApiBaseUrl } from "@/lib/config";

const CLIENT_ERROR_ENDPOINT = `${getApiBaseUrl()}/client-error-logs`;
const STORAGE_KEYS = {
  sessionId: "voxara-client-error-session-id",
  lastAction: "voxara-last-user-action",
  lastApiFailure: "voxara-last-api-failure",
  context: "voxara-error-context",
  lastSignature: "voxara-last-error-signature",
  lastSignatureAt: "voxara-last-error-signature-at",
};

const CLIENT_ERROR_DUPLICATE_SUPPRESSION_MS = 30_000;

let listenersAttached = false;

const safeSessionStorage = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (_error) {
      // ignore storage write failures
    }
  },
};

const readJson = (key, fallback = null) => {
  const raw = safeSessionStorage.get(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  safeSessionStorage.set(key, JSON.stringify(value));
};

export const getClientErrorSessionId = () => {
  if (typeof window === "undefined") return null;
  const existing = safeSessionStorage.get(STORAGE_KEYS.sessionId);
  if (existing) return existing;
  const generated = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : `client-error-${Date.now()}`;
  safeSessionStorage.set(STORAGE_KEYS.sessionId, generated);
  return generated;
};

export const updateErrorLoggingContext = (partialContext = {}) => {
  if (typeof window === "undefined") return;
  const current = readJson(STORAGE_KEYS.context, {}) || {};
  writeJson(STORAGE_KEYS.context, {
    ...current,
    ...partialContext,
    updatedAt: new Date().toISOString(),
  });
};

export const getLastAction = () => readJson(STORAGE_KEYS.lastAction, null);
export const getLastApiFailure = () => readJson(STORAGE_KEYS.lastApiFailure, null);

const getCurrentContext = () => readJson(STORAGE_KEYS.context, {}) || {};

const normalizeUrlPath = (url) => {
  if (!url) return null;
  try {
    return new URL(url, window.location.origin).pathname;
  } catch (_error) {
    return url;
  }
};

const makeSignature = (payload) => JSON.stringify([
  payload.category,
  payload.severity,
  payload.message,
  payload.route_path,
  normalizeUrlPath(payload.last_api_request?.url),
  payload.last_api_request?.status,
  payload.last_action?.testId,
]);

const shouldSuppressDuplicate = (payload) => {
  const signature = makeSignature(payload);
  const lastSignature = safeSessionStorage.get(STORAGE_KEYS.lastSignature);
  const lastAt = Number(safeSessionStorage.get(STORAGE_KEYS.lastSignatureAt) || 0);
  const now = Date.now();
  if (lastSignature === signature && now - lastAt < CLIENT_ERROR_DUPLICATE_SUPPRESSION_MS) {
    return true;
  }
  safeSessionStorage.set(STORAGE_KEYS.lastSignature, signature);
  safeSessionStorage.set(STORAGE_KEYS.lastSignatureAt, String(now));
  return false;
};

const getBrowserInfo = () => {
  if (typeof window === "undefined") return null;
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
};

export const storeLastApiFailure = (apiFailure) => {
  if (typeof window === "undefined") return;
  writeJson(STORAGE_KEYS.lastApiFailure, {
    ...apiFailure,
    occurredAt: new Date().toISOString(),
  });
};

export const logClientError = async (payload = {}) => {
  if (typeof window === "undefined") return;
  const context = getCurrentContext();
  const finalPayload = {
    source: payload.source || "frontend",
    category: payload.category || "client_error",
    severity: payload.severity || "error",
    title: payload.title || null,
    message: payload.message || "Unknown client error",
    stack: payload.stack || null,
    route_path: payload.route_path || context.routePath || `${window.location.pathname}${window.location.search}`,
    page_url: payload.page_url || window.location.href,
    client_session_id: getClientErrorSessionId(),
    browser_info: payload.browser_info || getBrowserInfo(),
    last_action: payload.last_action || getLastAction(),
    last_api_request: payload.last_api_request || getLastApiFailure(),
    context: {
      ...context,
      ...(payload.context || {}),
    },
  };

  if (shouldSuppressDuplicate(finalPayload)) {
    return;
  }

  try {
    const token = window.sessionStorage.getItem("voxara_token");
    const body = JSON.stringify(finalPayload);
    if (navigator.sendBeacon && !token) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(CLIENT_ERROR_ENDPOINT, blob);
      return;
    }
    await fetch(CLIENT_ERROR_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    });
  } catch (_error) {
    // avoid recursive logging loops
  }
};

export const logActionError = async ({ action, error, context = {}, title }) => {
  await logClientError({
    category: "action_error",
    severity: "error",
    title: title || action || "Action error",
    message: error?.response?.data?.detail || error?.message || `Action failed: ${action}`,
    stack: error?.stack || null,
    context: {
      action,
      ...context,
    },
  });
};

export const logApiFailure = async ({ method, url, status, message, responseData }) => {
  if (!url || url.includes("/client-error-logs") || url.includes("/admin/error-logs")) {
    return;
  }
  const normalizedMessage = String(message || "").toLowerCase();
  if ((status === 401 || status === 403) && /(token expired|not authenticated|could not validate credentials|invalid token|unauthorized|forbidden)/i.test(normalizedMessage)) {
    return;
  }
  const severity = status >= 500 || status === 0 ? "error" : "warning";
  const requestMeta = {
    method,
    url,
    status,
    message,
    responseData,
  };
  storeLastApiFailure(requestMeta);
  await logClientError({
    category: "api_error",
    severity,
    title: `${method || "API"} ${status || "ERR"}`,
    message: message || `API request failed: ${url}`,
    last_api_request: requestMeta,
  });
};

const captureAction = (event) => {
  const target = event.target?.closest?.("[data-testid],button,a,input,textarea,select") || event.target;
  if (!target) return;
  const payload = {
    type: event.type,
    testId: target.getAttribute?.("data-testid") || null,
    tagName: target.tagName || null,
    text: (target.innerText || target.value || "").trim().slice(0, 140),
    path: `${window.location.pathname}${window.location.search}`,
    occurredAt: new Date().toISOString(),
  };
  writeJson(STORAGE_KEYS.lastAction, payload);
};

export const bootstrapClientErrorLogging = () => {
  if (typeof window === "undefined" || listenersAttached) return;
  listenersAttached = true;
  getClientErrorSessionId();
  document.addEventListener("click", captureAction, true);
  document.addEventListener("submit", captureAction, true);
  window.addEventListener("error", (event) => {
    logClientError({
      category: "window_error",
      severity: "error",
      title: event.error?.name || "Window error",
      message: event.message || "Unhandled window error",
      stack: event.error?.stack || null,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    logClientError({
      category: "unhandled_rejection",
      severity: "error",
      title: "Unhandled promise rejection",
      message: reason?.message || String(reason || "Unhandled rejection"),
      stack: reason?.stack || null,
    });
  });
};